import React, { useState } from "react";
import { Layout, Menu, Avatar, Typography, Button, Flex, theme, Tooltip } from "antd";
import {
  HomeOutlined,
  FileTextOutlined,
  BankOutlined,
  ContainerOutlined,
  AppstoreOutlined,
  TeamOutlined,
  ShopOutlined,
  TagsOutlined,
  ShoppingOutlined,
  FileProtectOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { storageName } from "@/constans/auth";
import type { User } from "../types";

const { Sider } = Layout;
const { Text } = Typography;


const menuGroups = [
  {
    groupLabel: "General",
    items: [
      { key: "/dashboard", label: "Inicio", icon: <HomeOutlined /> },
      { key: "/empresa/dashboard", label: "Empresa", icon: <BankOutlined /> },
      { key: "/facturas", label: "Facturación", icon: <FileTextOutlined /> },
      { key: "/contratos", label: "Contratos", icon: <FileProtectOutlined /> },
      { key: "/cotizaciones", label: "Cotizaciones", icon: <ContainerOutlined /> },
    ],
  },
  {
    groupLabel: "Catálogos",
    items: [
      { key: "/catalogos", label: "Catálogos", icon: <AppstoreOutlined /> },
      { key: "/catalogo/proveedores", label: "Proveedores", icon: <ShopOutlined /> },
      { key: "/catalogo/clientes", label: "Clientes", icon: <TeamOutlined /> },
      { key: "/catalogo/conceptos", label: "Conceptos", icon: <TagsOutlined /> },
      { key: "/catalogo/productos", label: "Productos", icon: <ShoppingOutlined /> },
    ],
  },
];

const AppSidebar: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();

  const userData = localStorage.getItem(storageName);
  let user: User | null = null;
  if (userData) {
    try {
      user = JSON.parse(userData);
    } catch {
      // ignore
    }
  }
  const avatarLetter = user ? user.name.charAt(0).toUpperCase() : "U";
  const fullName = user
    ? `${user.name} ${user.lastname}${user.lastname2 || " "}`
    : "Usuario";

  const handleLogout = () => {
    localStorage.removeItem(storageName);
    navigate("/login");
  };

  const activeKey =
    menuGroups
      .flatMap((g) => g.items)
      .map((i) => i.key)
      .find((k) => location.pathname === k || location.pathname.startsWith(k + "/")) ??
    location.pathname;

  const menuItems = menuGroups.flatMap((group) => [
    { key: `group-${group.groupLabel}`, type: "group" as const, label: collapsed ? "" : group.groupLabel },
    ...group.items.map((item) => ({
      key: item.key,
      icon: item.icon,
      label: item.label,
      onClick: () => navigate(item.key),
    })),
  ]);

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      trigger={null}
      width={220}
      collapsedWidth={64}
      style={{
        height: "100vh",
        position: "sticky",
        top: 0,
        overflow: "hidden",
        background: token.colorBgContainer,
        borderRight: `1px solid ${token.colorBorderSecondary}`,
        display: "flex",
        flexDirection: "column",
        boxShadow: "2px 0 8px rgba(0,0,0,0.04)",
        zIndex: 200,
      }}
    >
      {/* ── Header ── */}
      <Flex
        align="center"
        justify={collapsed ? "center" : "space-between"}
        style={{
          padding: collapsed ? "16px 0" : "16px 16px 16px 20px",
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          height: 64,
          flexShrink: 0,
        }}
      >
        {!collapsed && (
          <Text
            strong
            style={{
              fontSize: 16,
              color: token.colorPrimary,
              letterSpacing: "-0.02em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {APP_NAME}
          </Text>
        )}
        <Button
          type="text"
          size="small"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => setCollapsed(!collapsed)}
          style={{ color: token.colorTextSecondary, flexShrink: 0 }}
        />
      </Flex>

      {/* ── User Profile ── */}
      <Flex
        align="center"
        gap={10}
        style={{
          padding: collapsed ? "12px 0" : "12px 16px",
          justifyContent: collapsed ? "center" : "flex-start",
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          flexShrink: 0,
        }}
      >
        <Avatar
          size={collapsed ? 32 : 36}
          style={{
            backgroundColor: token.colorPrimary,
            color: token.colorWhite,
            fontWeight: 700,
            flexShrink: 0,
            border: `2px solid ${token.colorPrimaryBorder}`,
          }}
        >
          {avatarLetter}
        </Avatar>
        {!collapsed && (
          <Flex vertical gap={0}>
            <Text style={{ fontSize: 12, fontWeight: 700, color: token.colorText, lineHeight: 1.3 }}>
              {fullName}
            </Text>
            <Text style={{ fontSize: 10, color: token.colorTextSecondary }}>Administrador</Text>
          </Flex>
        )}
      </Flex>

      {/* ── Menu ── */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        <Menu
          mode="inline"
          selectedKeys={[activeKey]}
          inlineCollapsed={collapsed}
          items={menuItems}
          style={{
            border: "none",
            background: "transparent",
            paddingTop: 8,
          }}
        />
      </div>

      {/* ── Footer Actions ── */}
      <Flex
        vertical
        gap={4}
        style={{
          padding: collapsed ? "12px 0" : "12px 12px",
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          flexShrink: 0,
          alignItems: collapsed ? "center" : "stretch",
        }}
      >

        <Tooltip title="Cerrar sesión" placement="right">
          <Button
            type="text"
            danger
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{
              justifyContent: collapsed ? "center" : "flex-start",
              paddingLeft: collapsed ? 0 : 12,
              width: "100%",
            }}
          >
            {!collapsed && "Cerrar sesión"}
          </Button>
        </Tooltip>
      </Flex>
    </Sider>
  );
};

export default AppSidebar;
